// Deeplink Landing Page — mobile entry point
// Reference: TECHNICAL DESIGN DOCUMENT — Section 7 (Web Redirect Page)
// Figma: Screens A/B/C share the same UI (spinner + "กรุณารอสักครู่..." +
// "ดาวน์โหลด Principal TH app" link). The state branch only affects
// behaviour, not visuals.

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ACTIONS,
  APP_OPEN_TIMEOUT_MS,
  FIREBASE_EVENTS,
  PLATFORMS,
  SCREENS,
  STORE_URLS,
  UI_STRINGS,
} from "./constants";
import { resolveDeeplink, trackEvent } from "./api";
import {
  buildAppSchemeUrl,
  detectPlatform,
  extractDestination,
  getStoreUrl,
  isPublicDestination,
  parseQuery,
  triggerAppScheme,
} from "./utils";
import ScreenDesktopPublic from "./ScreenDesktopPublic";
import pkg from "../../../package.json";
import "./style.scss";

function DeeplinkLanding() {
  const [lang, setLang] = useState("th");
  const [platform] = useState(() => detectPlatform());
  const [destination] = useState(() => extractDestination());
  const [resolveResult, setResolveResult] = useState(null);
  const [error, setError] = useState(null);

  const query = useRef(parseQuery()).current;
  const startedRef = useRef(false);

  const screen = useMemo(() => {
    if (platform === PLATFORMS.DESKTOP) {
      return isPublicDestination(destination)
        ? SCREENS.DESKTOP_PUBLIC
        : SCREENS.DESKTOP_PRIVATE;
    }
    return SCREENS.MOBILE_LANDING;
  }, [platform, destination]);

  const t = UI_STRINGS[lang] || UI_STRINGS.th;

  // --- Resolve + trigger cpamapp:// on mount (mobile only) ---
  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    if (platform === PLATFORMS.DESKTOP) {
      // Desktop branch is handled by ScreenDesktopPublic; just call /resolve
      // to fire the deeplink_click tracking event server-side.
      if (destination) {
        resolveDeeplink({
          destination,
          platform,
          user_state: query.user_state,
          lang: query.lang,
          ref_id: query.ref_id,
        })
          .then(result => {
            setResolveResult(result);
            trackEvent({
              event_name: FIREBASE_EVENTS.DEEPLINK_CLICK,
              destination,
              platform,
              ref_id: query.ref_id,
            });
          })
          .catch(err => {
            // eslint-disable-next-line no-console
            console.error("[deeplink] desktop resolve failed:", err);
          });
      }
      return;
    }

    if (!destination) {
      setError("Missing destination parameter");
      return;
    }

    const run = async () => {
      // Section 7.4 — Step 2. Try to resolve via backend, but treat it as
      // optional — be-deeplink may not be deployed yet. If the call fails
      // we fall back to client-side scheme construction + store URLs.
      let result = null;
      try {
        result = await resolveDeeplink({
          destination,
          platform,
          user_state: query.user_state,
          lang: query.lang,
          ref_id: query.ref_id,
        });
        setResolveResult(result);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("[deeplink] resolve unavailable, using scheme probe:", err?.message);
      }

      // Section 7.4 — Step 5
      trackEvent({
        event_name: FIREBASE_EVENTS.DEEPLINK_CLICK,
        destination,
        platform,
        user_state: query.user_state,
        ref_id: query.ref_id,
      });

      const storeUrl =
        platform === PLATFORMS.IOS
          ? (result && result.store_url_ios) || STORE_URLS.IOS
          : (result && result.store_url_android) || STORE_URLS.ANDROID;

      const goToStore = () => {
        trackEvent({
          event_name: FIREBASE_EVENTS.APP_STORE_REDIRECT,
          destination,
          platform,
          ref_id: query.ref_id,
        });
        window.location.assign(storeUrl);
      };

      // Section 4.1 — honour the routing decision from /resolve, but only when
      // /resolve actually answered with one.
      const action = result && result.action;
      const isValidAction =
        !!action && Object.values(ACTIONS).indexOf(action) !== -1;

      if (isValidAction) {
        if (action === ACTIONS.REDIRECT_STORE) {
          // App not installed → straight to the store, never touch the scheme.
          goToStore();
          return;
        }
        if (action === ACTIONS.REDIRECT_WEB && result.web_url) {
          window.location.assign(result.web_url);
          return;
        }
        if (action === ACTIONS.SHOW_LANDING_PAGE) {
          // Stay on this page — the manual download link remains available.
          return;
        }
      }

      // No usable answer from /resolve (backend unavailable, CORS-blocked, or
      // an error payload). Whether the app is installed is not something this
      // page or the backend can know here — no browser API exposes it, and
      // `app_installed` is never sent. Falling through to the scheme attempt is
      // what lets an installed app open; anyone without it uses the download
      // link, which is on the card from first paint.

      // open_app / open_app_login_first — or no usable /resolve answer.
      // Attempt to open the app. If it opens, the OS backgrounds this tab.
      //
      // The design is app-first: try the app, and only fall back to the store
      // if that attempt has not taken the customer off this page in time. See
      // the timer below and APP_OPEN_TIMEOUT_MS for why the window is what it
      // is — an earlier 1400ms expired while iOS was still showing its confirm
      // dialog, which is what sent people who had the app to the App Store.
      //
      // The /resolve `redirect_store` branch above is untouched — that one is a
      // decision from the backend, not a guess.
      const scheme =
        (result && result.app_scheme) ||
        buildAppSchemeUrl(destination, query.ref_id, query.lang, query);
      const start = Date.now();
      triggerAppScheme(scheme);

      // Give the app its chance first, and only fall back to the store if the
      // page is still on screen when the window closes. If the app opened, the
      // OS has backgrounded this tab and `document.hidden` is true, so the
      // person who already has the app is left alone.
      //
      // This is deliberately a single check rather than a set of
      // visibilitychange / pagehide / blur listeners. A version built that way
      // was intermittently unreliable in the field — stuck even when the app
      // was installed — and the reference implementation that does work on real
      // devices (principal.codediva.co.th/deeplink.html) uses exactly this.
      //
      // What went wrong before was the window, not the mechanism: at 1400ms it
      // expired while iOS was still showing its "Open this page in…" confirm,
      // so tapping Open a moment too late meant landing in the App Store with
      // the app already installed. See APP_OPEN_TIMEOUT_MS for the measurement
      // behind the current value.
      setTimeout(() => {
        if (!document.hidden && Date.now() - start >= APP_OPEN_TIMEOUT_MS) {
          goToStore();
        }
      }, APP_OPEN_TIMEOUT_MS);
    };

    run();
    // Intentionally run once on mount — `destination`, `platform`, and `query`
    // are derived from URL state captured at first render and never change.
  }, []);

  // --- Click handler for the download link ---
  // Detects the user's platform and sends them to the right store.
  const onDownloadAppClick = e => {
    e.preventDefault();
    trackEvent({
      event_name: FIREBASE_EVENTS.APP_STORE_REDIRECT,
      destination,
      platform,
      ref_id: query.ref_id,
    });

    const iosUrl = resolveResult?.store_url_ios || STORE_URLS.IOS;
    const androidUrl = resolveResult?.store_url_android || STORE_URLS.ANDROID;

    if (platform === PLATFORMS.IOS) {
      window.location.assign(iosUrl);
      return;
    }
    if (platform === PLATFORMS.ANDROID) {
      window.location.assign(androidUrl);
      return;
    }
    // Desktop fallback — open iOS store in a new tab (the desktop screen
    // exposes a richer download UX, so this is only reached if a desktop UA
    // somehow hits the mobile branch).
    window.open(getStoreUrl(platform), "_blank", "noopener,noreferrer");
  };

  // ---------- Desktop branches (Screen D / E) ----------

  if (screen === SCREENS.DESKTOP_PUBLIC) {
    return (
      <ScreenDesktopPublic
        destination={destination}
        resolveResult={resolveResult}
        query={query}
      />
    );
  }

  if (screen === SCREENS.DESKTOP_PRIVATE) {
    return (
      <ScreenDesktopPublic
        destination={destination}
        resolveResult={{ ...resolveResult, web_url: null }}
        query={query}
      />
    );
  }

  // ---------- Mobile landing (Screens A/B/C — identical UI) ----------

  return (
    <div className="dl-page">
      <header className="dl-header">
        <div className="dl-header__brand">
          <img
            className="dl-header__principal"
            src="/deeplink/principal-only.png"
            srcSet="/deeplink/principal-only.png 1x, /deeplink/principal-only@2x.png 2x, /deeplink/principal-only@3x.png 3x"
            alt="Principal"
          />
          <span className="dl-header__divider" aria-hidden="true" />
          <span className="dl-header__alliance" aria-label="In alliance with CIMB">
            {/* In alliance with [CIMB] — sourced from Figma node 7038-211812 */}
            <svg xmlns="http://www.w3.org/2000/svg" width="99" height="10" viewBox="0 0 99 10" fill="none">
              <path d="M81.3079 6.32069C80.9315 6.60574 80.4609 6.79578 79.9904 6.79578C78.8611 6.79578 78.0142 5.8456 78.0142 4.70538C78.0142 3.56517 78.8611 2.615 79.9904 2.615C80.4609 2.615 80.9315 2.80503 81.3079 3.09008L82.1549 2.23492C81.5902 1.75983 80.8374 1.37976 79.9904 1.37976C78.2024 1.37976 76.6967 2.90005 76.6967 4.70538C76.6967 6.51072 78.2024 8.03101 79.9904 8.03101C80.7433 8.03101 81.4961 7.74596 82.1549 7.17585L81.3079 6.32069ZM83.3783 7.84097H84.6017V1.5698H83.3783V7.84097ZM89.5893 5.56055C89.5893 5.46553 87.4249 1.5698 87.4249 1.5698H86.2015V7.84097H87.4249V4.13528C87.519 4.32531 89.5893 7.93599 89.5893 7.93599C89.5893 7.93599 91.6597 4.32531 91.7538 4.13528V7.84097H92.9772V1.5698H91.7538C91.7538 1.5698 89.5893 5.46553 89.5893 5.56055ZM96.8356 6.60574H95.6122V5.08546H96.8356C97.4002 5.08546 97.5884 5.46553 97.5884 5.8456C97.5884 6.22567 97.4002 6.60574 96.8356 6.60574ZM95.6122 2.615H96.7415C97.212 2.615 97.4943 2.90005 97.4943 3.28012C97.4943 3.75521 97.212 3.94524 96.7415 3.94524H95.6122V2.615ZM98.1531 4.61037C98.4354 4.42033 98.9059 4.04026 98.9059 3.28012C98.9059 2.23492 98.1531 1.47478 97.0238 1.47478H94.4829V7.74595H97.1179C98.3413 7.74595 99 7.08083 99 5.94062C98.9059 5.18048 98.4354 4.8004 98.1531 4.61037Z" fill="white"/>
              <path d="M0.846965 1.37927V7.65045H0V1.37927H0.846965Z" fill="white"/>
              <path d="M2.16466 4.32512C2.16466 3.85003 2.16466 3.46996 2.16466 3.08989H2.91752V3.85003C3.10574 3.37494 3.67038 2.99487 4.42323 2.99487C5.08198 2.99487 6.02306 3.37494 6.02306 4.89523V7.55572H5.17609V4.99025C5.17609 4.2301 4.89377 3.66 4.14091 3.66C3.57627 3.66 3.19984 4.04007 3.01163 4.51516C3.01163 4.61017 2.91752 4.80021 2.91752 4.89523V7.65074H2.07056V4.32512H2.16466Z" fill="white"/>
              <path d="M13.7275 7.70115L13.6334 7.13105C13.3511 7.51112 12.8805 7.79617 12.2218 7.79617C11.2807 7.79617 10.9043 7.13105 10.9043 6.46593C10.9043 5.42073 11.8454 4.7556 13.6334 4.7556V4.66059C13.6334 4.28052 13.5393 3.6154 12.5982 3.6154C12.2218 3.6154 11.7513 3.71041 11.4689 3.99546L11.2807 3.42536C11.6572 3.14031 12.2218 3.04529 12.7864 3.04529C14.198 3.04529 14.4804 3.99547 14.4804 4.85062V6.56094C14.4804 6.94101 14.4804 7.32108 14.5745 7.60613H13.7275V7.70115ZM13.5393 5.32571C12.6923 5.32571 11.6572 5.42073 11.6572 6.37091C11.6572 6.94101 12.0336 7.13105 12.41 7.13105C12.9747 7.13105 13.3511 6.75098 13.4452 6.37091C13.4452 6.27589 13.4452 6.18087 13.4452 6.08585V5.32571H13.5393Z" fill="white"/>
              <path d="M15.5172 1.05029H16.3642V7.70153H15.5172V1.05029Z" fill="white"/>
              <path d="M17.5032 1.00024H18.3501V7.65148H17.5032V1.00024Z" fill="white"/>
              <path d="M20.6079 1.85607C20.6079 2.14112 20.4197 2.33116 20.1374 2.33116C19.8551 2.33116 19.6669 2.14112 19.6669 1.85607C19.6669 1.57102 19.8551 1.38098 20.1374 1.38098C20.4197 1.38098 20.6079 1.57102 20.6079 1.85607ZM19.761 7.65215V3.18632H20.6079V7.65215H19.761Z" fill="white"/>
              <path d="M24.3724 7.65196L24.2783 7.08185C23.996 7.46192 23.5254 7.74697 22.8667 7.74697C21.9256 7.74697 21.5492 7.08185 21.5492 6.41673C21.5492 5.37153 22.4903 4.70641 24.2783 4.70641V4.61139C24.2783 4.23132 24.1842 3.5662 23.2431 3.5662C22.8667 3.5662 22.3962 3.66122 22.1138 3.94627L21.9256 3.37617C22.3021 3.09111 22.8667 2.99609 23.4313 2.99609C24.8429 2.99609 25.1253 3.94627 25.1253 4.80143V6.51174C25.1253 6.89181 25.1253 7.27188 25.2194 7.55694H24.3724V7.65196ZM24.2783 5.27652C23.4313 5.27652 22.3962 5.37153 22.3962 6.32171C22.3962 6.89181 22.7726 7.08185 23.149 7.08185C23.7137 7.08185 24.0901 6.70178 24.1842 6.32171C24.1842 6.22669 24.1842 6.13167 24.1842 6.03666V5.27652H24.2783Z" fill="white"/>
              <path d="M26.3485 4.32634C26.3485 3.85125 26.3485 3.47118 26.3485 3.09111H27.1014V3.85125C27.2896 3.37617 27.8542 2.99609 28.6071 2.99609C29.2658 2.99609 30.2069 3.37616 30.2069 4.89645V7.55694H29.3599V4.99147C29.3599 4.23132 29.0776 3.66122 28.3247 3.66122C27.7601 3.66122 27.3837 4.04129 27.1955 4.51638C27.1955 4.6114 27.1014 4.80143 27.1014 4.89645V7.65196H26.2544V4.32634H26.3485Z" fill="white"/>
              <path d="M34.6308 7.462C34.4425 7.55701 33.972 7.74705 33.3133 7.74705C31.9958 7.74705 31.0547 6.79687 31.0547 5.46663C31.0547 4.04136 31.9958 3.09119 33.4074 3.09119C33.8779 3.09119 34.3484 3.1862 34.5366 3.28122L34.3484 3.94634C34.1602 3.85133 33.8779 3.75631 33.4074 3.75631C32.3722 3.75631 31.8075 4.51645 31.8075 5.46663C31.8075 6.51182 32.4663 7.17694 33.3133 7.17694C33.7838 7.17694 34.0661 7.08193 34.3484 6.98691L34.6308 7.462Z" fill="white"/>
              <path d="M35.9143 5.56059C35.9143 6.70081 36.6672 7.08087 37.4201 7.08087C37.9847 7.08087 38.3611 6.98586 38.6435 6.89084L38.7376 7.46095C38.4552 7.55596 37.9847 7.746 37.2318 7.746C35.8202 7.746 35.0674 6.79582 35.0674 5.46558C35.0674 4.13533 35.8202 2.99512 37.1377 2.99512C38.5493 2.99512 39.0199 4.32536 39.0199 5.08551C39.0199 5.27554 39.0199 5.37056 39.0199 5.46558H35.9143V5.56059ZM38.3611 4.89547C38.3611 4.42038 38.1729 3.56523 37.2318 3.56523C36.3849 3.56523 36.0085 4.32536 36.0085 4.89547H38.3611Z" fill="white"/>
              <path d="M44.511 3.09119L45.0757 5.37161C45.1698 5.8467 45.358 6.32178 45.4521 6.79687C45.5462 6.32178 45.7344 5.8467 45.8285 5.37161L46.5814 3.09119H47.2401L47.8989 5.37161C48.0871 5.94171 48.1812 6.4168 48.2753 6.79687C48.3694 6.32178 48.4635 5.8467 48.6518 5.37161L49.3105 3.09119H50.1575L48.7459 7.55701H47.993L47.3342 5.37161C47.146 4.89652 47.0519 4.42143 46.9578 3.85133C46.8637 4.42143 46.6755 4.89652 46.5814 5.37161L45.8285 7.55701H45.0757L43.6641 3.09119H44.511Z" fill="white"/>
              <path d="M51.8517 1.85558C51.8517 2.14063 51.6635 2.33067 51.3812 2.33067C51.0989 2.33067 50.9106 2.14063 50.9106 1.85558C50.9106 1.57053 51.0989 1.38049 51.3812 1.38049C51.6635 1.38049 51.8517 1.57053 51.8517 1.85558ZM50.9106 7.65166V3.18583H51.7576V7.65166H50.9106Z" fill="white"/>
              <path d="M54.1103 1.85583V3.18608H55.2396V3.85121H54.1103V6.32167C54.1103 6.89177 54.2985 7.17683 54.675 7.17683C54.8632 7.17683 55.0514 7.17683 55.1455 7.08181V7.65192C54.9573 7.74693 54.7691 7.74693 54.3927 7.74693C54.0162 7.74693 53.7339 7.65192 53.5457 7.36686C53.3575 7.08181 53.2634 6.70174 53.2634 6.22665V3.75619H52.6046V3.09107H53.2634V2.04587L54.1103 1.85583Z" fill="white"/>
              <path d="M56.1818 1.00049H57.0287V3.85102C57.1228 3.66098 57.4052 3.37593 57.5934 3.28091C57.8757 3.18589 58.158 3.09088 58.4403 3.09088C59.005 3.09088 60.0401 3.47095 60.0401 4.99123V7.65172H59.1932V5.08624C59.1932 4.3261 58.9109 3.756 58.158 3.756C57.5934 3.756 57.2169 4.13607 57.0287 4.61116C57.0287 4.70617 56.9346 4.80119 56.9346 4.99123V7.74674H56.0876V1.00049H56.1818Z" fill="white"/>
              <path d="M74 0H64V10H74V0Z" fill="white"/>
              <path d="M70.5065 2H65L67.3896 5H73L70.5065 2Z" fill="#ED1C24"/>
              <path d="M73 5H67.3896L65 8H70.5065L73 5Z" fill="#790008"/>
            </svg>
          </span>
        </div>
        <div className="dl-header__lang" role="tablist" aria-label="ภาษา">
          <button
            type="button"
            className={
              "dl-header__lang-btn" + (lang === "th" ? " is-active" : "")
            }
            aria-selected={lang === "th"}
            onClick={() => setLang("th")}
          >
            {t.langTh}
          </button>
          <button
            type="button"
            className={
              "dl-header__lang-btn" + (lang === "en" ? " is-active" : "")
            }
            aria-selected={lang === "en"}
            onClick={() => setLang("en")}
          >
            {t.langEn}
          </button>
        </div>
      </header>

      <main>
        <section className="dl-card">
          <div className="dl-spinner" aria-label={t.title}>
            {/* 8-bar circular spinner from Figma node 7038-211812 */}
            <svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 80 80" fill="none">
              <g>
                <path d="M40 0C37.7736 0 35.9685 1.80494 35.9685 4.03153V18.0744C35.9685 20.301 37.7734 22.1059 40 22.1059C42.2266 22.1059 44.0316 20.3012 44.0316 18.0744V4.03153C44.0316 1.80511 42.2265 0 40 0Z" fill="#0091DA"/>
                <path d="M27.347 21.6455L17.4173 11.7157C15.8429 10.1413 13.2903 10.1413 11.7157 11.7157C10.1413 13.2901 10.1413 15.8427 11.7157 17.4171L21.6456 27.347C23.22 28.9216 25.7726 28.9216 27.347 27.347C28.9214 25.7725 28.9214 23.2199 27.347 21.6455Z" fill="#0091DA"/>
                <path d="M18.0746 35.9686H4.03153C1.80494 35.9686 0 37.7736 0 40.0002C0 42.2267 1.80494 44.0317 4.03153 44.0317H18.0746C20.3012 44.0317 22.1061 42.2267 22.1061 40.0002C22.1059 37.7736 20.301 35.9686 18.0746 35.9686Z" fill="#0091DA"/>
                <path d="M27.347 52.6528C25.7726 51.0783 23.22 51.0783 21.6456 52.6528L11.7157 62.5828C10.1413 64.1572 10.1413 66.7098 11.7157 68.2842C13.2903 69.8586 15.8429 69.8586 17.4173 68.2842L27.347 58.3544C28.9214 56.78 28.9214 54.2274 27.347 52.6528Z" fill="#0091DA"/>
                <path d="M40 57.894C37.7736 57.894 35.9685 59.699 35.9685 61.9256V75.9684C35.9685 78.195 37.7734 80 40 80C42.2266 80 44.0316 78.195 44.0316 75.9684V61.9256C44.0316 59.6992 42.2265 57.894 40 57.894Z" fill="#0091DA"/>
                <path d="M68.2844 62.583L58.3546 52.6531C56.7801 51.0785 54.2276 51.0785 52.6531 52.6531C51.0787 54.2276 51.0787 56.7803 52.6531 58.3546L62.583 68.2844C64.1574 69.8588 66.71 69.8588 68.2844 68.2844C69.8588 66.71 69.8588 64.1573 68.2844 62.583Z" fill="#0091DA"/>
                <path d="M75.9686 35.9686H61.9255C59.6989 35.9686 57.8938 37.7734 57.8938 40.0002C57.8938 42.2267 59.6987 44.0317 61.9255 44.0317H75.9686C78.1952 44.0317 80.0001 42.2267 80.0001 40.0002C79.9999 37.7736 78.195 35.9686 75.9686 35.9686Z" fill="#0091DA"/>
                <path d="M68.2844 11.7157C66.71 10.1413 64.1574 10.1413 62.583 11.7157L52.6531 21.6455C51.0787 23.2199 51.0787 25.7725 52.6531 27.347C54.2276 28.9216 56.7801 28.9216 58.3546 27.347L68.2844 17.4171C69.8588 15.8427 69.8588 13.2901 68.2844 11.7157Z" fill="#0091DA"/>
              </g>
            </svg>
          </div>
          <h1 className="dl-card__title">{t.title}</h1>
          <p className="dl-card__subtitle">{t.subtitle}</p>
          <button
            type="button"
            className="dl-card__download"
            onClick={onDownloadAppClick}
          >
            <span className="dl-card__download-link">{t.downloadLink}</span>
            <span className="dl-card__download-arrow" aria-hidden="true">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M4 12H20" stroke="#0091DA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M14 6L20 12L14 18" stroke="#0091DA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </span>
          </button>
        </section>

        {error && (
          <p className="dl-error">ไม่สามารถเปิดลิงก์นี้ได้ในขณะนี้</p>
        )}
      </main>

      <span className="dl-version">v{pkg.version}</span>
    </div>
  );
}

export default DeeplinkLanding;
